import { useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import {
  whenAuthReady,
  getCachedSession,
  isAuthReadyNow,
  subscribeAuth,
} from "@/lib/auth-ready";

/**
 * Shared, deduplicated auth-readiness hook.
 *
 * - `isReady` flips true once the Supabase client has finished restoring its
 *   session from storage at least once. Until then, RLS-protected queries
 *   should NOT fire — they will silently return zero rows and surface as
 *   "not found" until the user reloads.
 * - All consumers share one underlying `onAuthStateChange` listener, so
 *   adding `useAuthReady()` to a page does not multiply auth events.
 */
export function useAuthReady(): { isReady: boolean; session: Session | null; user: User | null } {
  const [session, setSession] = useState<Session | null>(getCachedSession());
  const [isReady, setIsReady] = useState<boolean>(isAuthReadyNow());

  useEffect(() => {
    let mounted = true;
    if (!isReady) {
      whenAuthReady().then((s) => {
        if (!mounted) return;
        setSession(s);
        setIsReady(true);
      });
    }
    const unsub = subscribeAuth((s) => {
      if (!mounted) return;
      setSession(s);
      if (!isReady) setIsReady(true);
    });
    return () => {
      mounted = false;
      unsub();
    };
    // We intentionally only run once on mount; subscribeAuth keeps us in sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isReady, session, user: session?.user ?? null };
}